/* eslint-disable no-alert */
import React, { useState, useEffect, FC } from 'react';
import queryString from 'querystring';
import { useStatus, StatusContext } from 'src/components/status-store';
import { Button } from '@domain-group/fe-co-button';
import { SystemConfigViewModel } from '../../lib/portal-api';
import * as PortalApi from '../../lib/portal-api';
import * as gtag from '../../lib/gtag';
import { SwaggerContextState } from './swaggerContext';
import ProjectList from './projectList';
import CredentialList from './credentialList';
import SecretList from './secretList';
import DemoConfiguration from './demoConfiguration';
import * as styles from './authoriser.styles';

interface AuthoriserProps {
  swaggerContext: SwaggerContextState;
  systemConfig: SystemConfigViewModel;
}

interface SwaggerAuthoriserState {
  chosenProject?: PortalApi.ProjectViewModel;
  chosenCredential?: PortalApi.CredentialViewModel;
  chosenSecret?: PortalApi.SecretViewModel;
  canAuthenticate: boolean;
}

declare global {
  interface Window {
    authParameters: InteractiveAuthorisationParameters;
    swaggerContext: SwaggerContextState;
  }
}

interface InteractiveAuthorisationParameters {
  authority: string;
  redirectUri: string;
  environment: PortalApi.ProjectEnvironment;
  clientId: string;
  clientSecret: string;
  flow: PortalApi.AuthFlow;
}

// eslint-disable-next-line camelcase
const JSON_to_URLEncoded = (element: any, key?: any, list?: any): any => {
  const updatedList = list || [];
  if (typeof element === 'object') {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const idx in element) {
      JSON_to_URLEncoded(
        element[idx],
        key ? `${key}[${idx}]` : idx,
        updatedList,
      );
    }
  } else {
    updatedList.push(`${key}=${encodeURIComponent(element)}`);
  }
  return updatedList.join('&');
};

const getImplicitToken = (
  authority: string,
  redirectUri: string,
  environment: PortalApi.ProjectEnvironment,
  clientId: string,
  scopes: string,
) => {
  window.authParameters = {
    authority,
    redirectUri,
    clientId,
    clientSecret: '', // no secret required for implicit
    flow: 'implicit',
    environment,
  };
  const authUrl = `${authority}connect/authorize?client_id=${clientId}&prompt=login&redirect_uri=${redirectUri}&response_type=token&scope=${scopes}&nonce=1234&state=1234`;
  window.open(authUrl, 'authoriser', 'toolbar=1,status=1,width=548,height=325');
};

const getAuthCodeToken = async (
  authority: string,
  redirectUri: string,
  environment: PortalApi.ProjectEnvironment,
  clientId: string,
  clientSecret: string,
  scopes: string,
) => {
  window.authParameters = {
    authority,
    redirectUri,
    clientId,
    clientSecret,
    flow: 'authorizationCode',
    environment,
  };
  window.open(
    `${authority}connect/authorize?client_id=${clientId}&prompt=login&redirect_uri=${redirectUri}&response_type=code&scope=openid%20profile%20${scopes}&nonce=1234&state=1234`,
    'authoriser',
    'toolbar=1,status=1,width=548,height=325',
  );
};

const getClientCredentialsToken = async (
  clientId: string,
  secret: string,
  scopes: string,
  authority: string,
) => {
  const tokenFetchData = {
    grant_type: 'client_credentials',
    scope: scopes,
  };

  const authResponse = await fetch(`${authority}connect/token`, {
    body: JSON_to_URLEncoded(tokenFetchData),
    credentials: 'omit',
    method: 'POST',
    mode: 'cors',
    headers: new Headers({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${btoa(`${clientId}:${secret}`)}`,
    }),
  });

  const tokenData = await authResponse.json();
  // TODO: this.props.reactGa && this.props.reactGa.event(gaEvents.docsLiveUiGetTokenResponseReceived(response.status.toString(), response.ok));
  return tokenData.access_token;
};

const receiveAuthCodeToken = async (event: MessageEvent) => {
  // must check something unique about the data blob...
  // some of the react dev tools absolutely spam the message system
  if (
    !event.data ||
    !event.data.source ||
    event.data.source !== 'oauth2callback'
  ) {
    return;
  }

  // assert the event is from us
  if (event.origin !== window.location.origin) {
    return;
  }

  // we need to obtain the state in this event handler which does not have access to the react component internal state
  const authParameters = window.authParameters;

  if (authParameters.flow === 'implicit') {
    if (event.data.hash) {
      const authHash = event.data.hash.replace(/#access_token/, 'access_token');
      const authDetails = queryString.parse(authHash);
      if (authDetails.access_token) {
        // this.setToken(authDetails.access_token);
        window.swaggerContext.authenticate(
          authParameters.environment,
          authDetails.access_token.toString(),
          'oAuthClient',
          authParameters.flow,
          authParameters.clientId,
        );
        // this.props.reactGa && this.props.reactGa.event(gaEvents.docsLiveUiGetTokenResponseReceived('200', true));
        return;
      }
    }
    alert('No token found');
    // this.props.reactGa && this.props.reactGa.event(gaEvents.docsLiveUiGetTokenResponseReceived('N/A', false));
    return;
  }

  const authString = queryString.parse(event.data.href.split('?')[1]);

  const redeemCodeData = {
    grant_type: 'authorization_code',
    redirect_uri: authParameters.redirectUri,
    code: authString.code,
  };

  const bodyContent = JSON_to_URLEncoded(redeemCodeData);

  const authResponse = await fetch(`${authParameters.authority}connect/token`, {
    body: bodyContent,
    credentials: 'omit',
    method: 'POST',
    mode: 'cors',
    headers: new Headers({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${btoa(
        `${authParameters.clientId}:${authParameters.clientSecret}`,
      )}`,
    }),
  });

  const authData = await authResponse.json();
  window.swaggerContext.authenticate(
    authParameters.environment,
    authData.access_token.toString(),
    'oAuthClient',
    authParameters.flow,
    authParameters.clientId,
  );
};

const Authoriser: FC<AuthoriserProps> = ({ swaggerContext, systemConfig }) => {
  const status = useStatus();
  const [authoriserState, updateAuthoriserState] =
    useState<SwaggerAuthoriserState>({
      canAuthenticate: false,
    });

  useEffect(() => {
    // ? https://usehooks.com/useEventListener/
    window.swaggerContext = swaggerContext;
    window.addEventListener('message', receiveAuthCodeToken);
  }, [swaggerContext]);

  const changeSelectedProject = async (
    chosenProject: PortalApi.ProjectViewModel,
  ) => {
    updateAuthoriserState({
      chosenProject,
      chosenCredential: undefined,
      chosenSecret: undefined,
      canAuthenticate: false,
    });

    const packages = await PortalApi.getPackages(
      status.accessToken,
      chosenProject.id,
    );
    const availableEndpoints: string[] = [];
    packages.forEach((pkg) => availableEndpoints.push(...pkg.operations));
    swaggerContext.setProjectId(chosenProject.id);
    swaggerContext.setAvailableOperations(
      availableEndpoints.map((_op) => _op.toLowerCase()),
    );

    gtag.event({
      category: 'docs-live-ui',
      action: 'select-project',
      label: chosenProject.id,
    });
  };

  // Purpose of "allowAuthentication":
  // - After "demoConfiguration", this is called and should reset the chosen secret
  // - Because the chosen credential id doesn't actually change...
  //    - The nested change events do not fire (expected behaviour of useEffect)
  //    - The secret list doesn't refresh
  //    - The canAuthenticate is not re-evaluated in the changeSelectedSecret method
  // This assumes:
  //  - the chosen secret was valid
  //  - which it should be because demoConfiguration only fires if canAuthenticate was otherwise true
  const changeSelectedCredential = (
    chosenCredential: PortalApi.CredentialViewModel,
    allowAuthentication = false,
  ) => {
    updateAuthoriserState({
      chosenProject: authoriserState.chosenProject,
      chosenCredential,
      chosenSecret: undefined,
      canAuthenticate: !!(
        allowAuthentication ||
        chosenCredential.credentialType === 'apiKey' ||
        chosenCredential.flow === 'implicit'
      ),
    });

    gtag.event({
      category: 'docs-live-ui',
      action: 'select-client',
      label: chosenCredential.id,
    });
  };

  const changeSelectedSecret = (chosenSecret: PortalApi.SecretViewModel) => {
    updateAuthoriserState({
      chosenProject: authoriserState.chosenProject,
      chosenCredential: authoriserState.chosenCredential,
      chosenSecret,
      canAuthenticate: !!chosenSecret,
    });

    gtag.event({
      category: 'docs-live-ui',
      action: 'select-secret',
      label: chosenSecret.id,
    });
  };

  const getRedirectUri = () => `${window.location.origin}/oauth2callback.html`;

  const requiresConfiguration = () => {
    if (authoriserState.chosenCredential?.flow === 'clientCredentials') {
      return false;
    } // clientCreds does not need a redirect url

    // oauth + implicit and authcode need the redirect url in
    return !authoriserState.chosenCredential?.redirectUris.includes(
      getRedirectUri(),
    );
  };

  const authenticate = async () => {
    if (!authoriserState.canAuthenticate) {
      throw new Error('Cannot authenticate yet');
    }

    gtag.event({
      category: 'docs-live-ui',
      action: 'get-token-started',
      label:
        authoriserState.chosenCredential?.credentialType ?? 'unknown-auth-type',
    });

    switch (authoriserState.chosenCredential?.credentialType) {
      case 'apiKey':
        swaggerContext.authenticate(
          authoriserState.chosenProject?.environment ?? 'primary',
          authoriserState.chosenCredential.authClientId,
          authoriserState.chosenCredential.credentialType,
          authoriserState.chosenCredential.flow,
          authoriserState.chosenCredential.authClientId,
        );
        break;
      case 'oAuthClient': {
        const scopes = (authoriserState.chosenCredential.allowedScopes ?? [])
          .join(' ')
          .replace(/openid/, '')
          .replace(/offline_access/, '')
          .replace(/public-basic-profile/, '')
          .replace(/profile/, '')
          .replace(/email/, '');
        switch (authoriserState.chosenCredential?.flow) {
          case 'clientCredentials': {
            const clientCredentialSecret = await PortalApi.getSecret(
              status.accessToken,
              authoriserState.chosenCredential.id,
              authoriserState.chosenSecret?.id ?? '',
            );
            const token = await getClientCredentialsToken(
              authoriserState.chosenCredential.authClientId,
              clientCredentialSecret?.secret ?? '',
              scopes,
              systemConfig.authenticationAuthority,
            );
            if (token !== '') {
              swaggerContext.authenticate(
                authoriserState.chosenProject?.environment ?? 'primary',
                token,
                authoriserState.chosenCredential.credentialType,
                authoriserState.chosenCredential.flow,
                authoriserState.chosenCredential.authClientId,
              );
            }
            break;
          }
          case 'authorizationCode': {
            const authCodeSecret = await PortalApi.getSecret(
              status.accessToken,
              authoriserState.chosenCredential.id,
              authoriserState.chosenSecret?.id ?? '',
            );
            getAuthCodeToken(
              systemConfig.authenticationAuthority,
              getRedirectUri(),
              authoriserState.chosenProject?.environment ?? 'sandbox',
              authoriserState.chosenCredential.authClientId,
              authCodeSecret?.secret ?? '',
              scopes,
            );
            break;
          }
          case 'implicit':
            getImplicitToken(
              systemConfig.authenticationAuthority,
              getRedirectUri(),
              authoriserState.chosenProject?.environment ?? 'sandbox',
              authoriserState.chosenCredential.authClientId,
              scopes,
            );
            break;
          default:
            alert('Not Supported');
            break;
        }

        break;
      }
      default:
        alert('Not Supported');
        break;
    }
  };

  // allow choosing a new swagger token... if logged in
  return (
    <StatusContext.Consumer>
      {(statusContext) => (
        <div css={styles.container}>
          {statusContext.status?.isAuthenticated &&
          statusContext.status?.teamId ? (
            <>
              <ProjectList
                teamId={statusContext.status.teamId}
                onChange={changeSelectedProject}
              />
              {authoriserState.chosenProject ? (
                <CredentialList
                  projectId={authoriserState.chosenProject.id}
                  onChange={changeSelectedCredential}
                />
              ) : (
                <></>
              )}
              {authoriserState.chosenCredential &&
              authoriserState.chosenCredential.credentialType !== 'apiKey' &&
              authoriserState.chosenCredential.flow !== 'implicit' ? (
                <SecretList
                  credentialId={authoriserState.chosenCredential.id}
                  onChange={changeSelectedSecret}
                />
              ) : (
                <></>
              )}
              {authoriserState.canAuthenticate && requiresConfiguration() && (
                <DemoConfiguration
                  credentialId={authoriserState.chosenCredential?.id || ''}
                  callbackUrl={getRedirectUri()}
                  callbackFunction={changeSelectedCredential}
                />
              )}
              {authoriserState.canAuthenticate && !requiresConfiguration() && (
                <p>
                  <Button
                    appearance="regular"
                    size="small"
                    onClick={async () => authenticate()}
                  >
                    Authenticate
                  </Button>
                </p>
              )}
            </>
          ) : (
            <p>
              Your registered projects will automatically appear here to try it
              out when you are logged in.
            </p>
          )}
        </div>
      )}
    </StatusContext.Consumer>
  );
};

export default Authoriser;
